/**
 * Ticket 114.3 — useDemos hook.
 *
 * Loads the list of demos for the current tenant. Supports simple
 * client-side filtering by status, industryKey, opportunity (text search
 * on businessName/slug), and owner (createdBy UUID match).
 *
 * Uses useEffect + useState (matching the project's existing pattern
 * — no TanStack Query dependency added in this phase).
 */

import { useCallback, useEffect, useState } from 'react';
import { listDemos, type Demo } from '@/api/demos';

export interface DemoFilters {
  status?: string;
  industryKey?: string;
  /** Free-text match against businessName */
  opportunity?: string;
  /** Match against createdBy UUID */
  ownerId?: string;
}

export function useDemos(filters?: DemoFilters) {
  const [demos, setDemos] = useState<Demo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(() => {
    setLoading(true);
    setError(null);
    listDemos()
      .then((all) => {
        let filtered = all;
        if (filters?.status) {
          filtered = filtered.filter((d) => d.status === filters.status);
        }
        if (filters?.industryKey) {
          filtered = filtered.filter((d) => d.industryKey === filters.industryKey);
        }
        if (filters?.opportunity) {
          const q = filters.opportunity.toLowerCase();
          filtered = filtered.filter(
            (d) =>
              d.businessName.toLowerCase().includes(q) ||
              d.slug.toLowerCase().includes(q),
          );
        }
        if (filters?.ownerId) {
          filtered = filtered.filter((d) => d.createdBy === filters.ownerId);
        }
        setDemos(filtered);
      })
      .catch(() => setError('demos.list.loadError'))
      .finally(() => setLoading(false));
  }, [filters?.status, filters?.industryKey, filters?.opportunity, filters?.ownerId]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { demos, loading, error, reload };
}
